/**
 * Billing Service — chamadas à API REST `/billing/*`.
 *
 * Esta é a Phase 1 (foundation): apenas leitura/criação do BillingCustomer
 * por tenant. Subscriptions/invoices virão em fases futuras.
 */

import type { AxiosError } from 'axios';
import { api } from './api';

export type DocumentType = 'CPF' | 'CNPJ';

/**
 * Endereço de cobrança. Enviado ao checkout hospedado do Asaas como
 * `customerData` (pré-preenchimento). `number` e `neighborhood` são campos
 * separados porque o Asaas exige o conjunto completo de endereço.
 */
export interface BillingAddress {
  /** Logradouro. */
  street: string;
  /** Número. */
  number: string;
  /** Complemento (opcional). */
  complement?: string | null;
  /** Bairro. */
  neighborhood: string;
  /** Cidade. */
  city: string;
  /** UF. */
  state: string;
  /** CEP (apenas dígitos). */
  zip: string;
}

export interface BillingCustomer {
  id: string;
  organizationId: string;
  tenantId: string;
  gatewayCustomerId: string;
  legalName: string;
  tradeName?: string | null;
  documentType: DocumentType;
  documentNumber: string;
  email: string;
  phone?: string | null;
  billingAddress?: BillingAddress | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBillingCustomerDTO {
  legalName: string;
  tradeName?: string;
  documentType: DocumentType;
  /** CPF (11) ou CNPJ (14), apenas dígitos. */
  documentNumber: string;
  email: string;
  phone?: string;
  billingAddress: BillingAddress;
}

// ============================================================================
// Plans (catálogo voltado ao cliente — PR5a `GET /billing/plans`)
// ============================================================================

/**
 * Ciclo de cobrança escolhido na assinatura/estimativa.
 * - `MONTHLY` (default): preço/cobrança mensal.
 * - `YEARLY`: preço/cobrança anual (os campos `annual*` do plano já codificam o
 *   desconto de ~20%).
 */
export type BillingCycle = 'MONTHLY' | 'YEARLY';

/**
 * Modelo de cobrança do plano (espelha o backend).
 * - `per_seat`: preço por assento (base = preço × assentos inclusos).
 * - `base_plus_seat`: base fixa + valor por assento adicional.
 */
export type BillingPlanModel = 'per_seat' | 'base_plus_seat';

/**
 * Limites configuráveis de um plano. Campos `null`/ausentes representam
 * "Ilimitado" (ex.: limites de Fluxos/automações).
 */
export interface BillingPlanLimits {
  flowActiveLimit?: number | null;
  flowDailyExecutionLimit?: number | null;
  flowMonthlyExecutionLimit?: number | null;
  automationLimit?: number | null;
  userLimit?: number | null;
  contactLimit?: number | null;
  whatsappInstanceLimit?: number | null;
}

/** Recursos (feature toggles) habilitados por um plano (espelha `TenantFeatures`). */
export interface BillingPlanFeatures {
  automations?: boolean;
  whatsapp?: boolean;
  /** "API Meta oficial" — provedores oficiais do WhatsApp. */
  whatsapp_official_api?: boolean;
  /** Campanhas de WhatsApp — efetiva só com a API oficial (`hasWhatsAppCampaigns`). */
  whatsapp_campaigns?: boolean;
  reports?: boolean;
  api?: boolean;
  /** Copiloto de IA (sugestões/resumos sob demanda no atendimento). */
  ai_copilot?: boolean;
  /** Agente de atendimento (auto-reply reativo no WhatsApp). */
  ai_autopilot?: boolean;
  /** Temperatura do Lead por IA (lead scoring). */
  ai_lead_scoring?: boolean;
  /** Transcrição de áudio do WhatsApp. */
  ai_transcription?: boolean;
  /** SDR autônomo. */
  ai_sdr?: boolean;
}

/**
 * Plano ATIVO da organização, voltado ao cliente (`GET /billing/plans`).
 *
 * Valores monetários sempre em centavos (integer). Preços `null` =
 * "sob consulta" (ex.: plano Enterprise) — NÃO é self-serve (o backend rejeita
 * a assinatura com 422 `BUSINESS_BILLING_PLAN_NOT_SELF_SERVE`).
 */
export interface BillingPlan {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  displayOrder: number;
  isFeatured: boolean;
  billingModel: BillingPlanModel;
  /** Base mensal em centavos. `null` = "sob consulta". */
  monthlyBaseCents?: number | null;
  /** Base anual em centavos. `null` = "sob consulta". */
  annualBaseCents?: number | null;
  /** Valor mensal por usuário extra em centavos. `null` = "sob consulta". */
  extraUserMonthlyCents?: number | null;
  /** Valor anual por usuário extra em centavos. `null` = "sob consulta". */
  extraUserAnnualCents?: number | null;
  /**
   * Valor mensal do add-on "WhatsApp extra" em centavos. `null` = o plano NÃO
   * oferece o add-on (Starter/Enterprise). Só Essencial/Pro/Business têm preço.
   */
  extraWhatsappMonthlyCents?: number | null;
  /** Valor anual do add-on "WhatsApp extra" em centavos. `null` = sem add-on. */
  extraWhatsappAnnualCents?: number | null;
  /** Usuários inclusos na base. `null` = ilimitado/sob consulta. */
  includedUsers?: number | null;
  /** Instâncias de WhatsApp inclusas. `null` = ilimitado/sob consulta. */
  includedWhatsapp?: number | null;
  limits: BillingPlanLimits;
  features: BillingPlanFeatures;
}

export type BillingAccessState = 'allowed' | 'trial' | 'paywall' | 'org_suspended';

export interface BillingAccessSubscription {
  id: string;
  status: string;
  seats: number;
  currentTermEnd: string | null;
  trialEnd: string | null;
  nextBillingAt: string | null;
}

/**
 * Resposta de `GET /billing/access` (Epic #884 §7, Fase 8).
 * `isBillingOwner` gateia a aba Faturamento no frontend.
 */
export interface BillingAccess {
  isBillingOwner: boolean;
  accessState: BillingAccessState;
  blockCode: string | null;
  trialEndsAt: string | null;
  subscription: BillingAccessSubscription | null;
  hasValidCard: boolean;
  /** Tenant ISENTO DE PAGAMENTO — frontend oculta o menu/aba Faturamento. */
  exempt: boolean;
}

/**
 * GET /billing/access — estado de billing/owner do tenant atual.
 *
 * Acessível por qualquer usuário autenticado do tenant (não exige ser billing
 * owner) — usado para decidir o gating da aba Faturamento.
 */
export async function getBillingAccess(): Promise<BillingAccess> {
  const { data } = await api.get<BillingAccess>('/billing/access');
  return data;
}

/**
 * GET /billing/customer — retorna o customer do tenant atual.
 * Retorna `null` quando o backend responde 404 (ainda não criado).
 */
export async function getBillingCustomer(): Promise<BillingCustomer | null> {
  try {
    const { data } = await api.get<BillingCustomer>('/billing/customer');
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    if (axiosError?.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * POST /billing/customer — cria customer no gateway de pagamento + mirror local.
 */
export async function createBillingCustomer(
  payload: CreateBillingCustomerDTO,
): Promise<BillingCustomer> {
  const { data } = await api.post<BillingCustomer>('/billing/customer', payload);
  return data;
}

/**
 * GET /billing/plans — planos ATIVOS da organização (ordenados por
 * `displayOrder`), voltados ao cliente (PR5a). Usado para montar os cards de
 * plano + o toggle mensal/anual na tela de Faturamento.
 */
export async function listPlans(): Promise<BillingPlan[]> {
  const { data } = await api.get<BillingPlan[]>('/billing/plans');
  return data;
}

export const billingService = {
  getBillingAccess,
  getBillingCustomer,
  createBillingCustomer,
  listPlans,
};
